/**
 * Whether a signed-in member may open an admin page for this club.
 *
 * Pure, so the rule behind `middleware/auth.js` can be tested; the middleware
 * only reads the route and calls this.
 *
 * Two things this fixes, both found by the September 2026 compliance run:
 *  - a SUPER admin was bounced off every other club's admin pages to their own
 *    dashboard, because only `adminFor` was consulted and the super flag never
 *    was. The console exists so that a super admin can open any club; being
 *    silently swapped to `/admin` looked like a general deep-link defect and
 *    cost two windows time before it was named.
 *  - `adminFor` holds lowercase GUIDs and the comparison was case-sensitive,
 *    so an upper-case club id in a URL failed the same way. A GUID is not
 *    case-sensitive anywhere else in the product.
 */
export function canOpenClubAdmin(user, clubId) {
  if (!user) return false;
  if (user.superAdmin) return true;
  if (!clubId) return false;
  const wanted = String(clubId).toLowerCase();
  return (user.adminFor || []).some((id) => String(id).toLowerCase() === wanted);
}

/**
 * Whether the admin console's lower menu offers "Switch club".
 *
 * Every club admin, not only those with two or more clubs. The entry opens
 * `/admin?switchClub=true`, the "Your clubs" page, which is also where the
 * "Create a club" card lives; and `redirects.global.js` sends a single-club
 * admin who opens plain `/admin` straight to their club's dashboard, so this
 * entry is the only way such an admin reaches that card from the console.
 * Hiding it for a single club (26 Aug 2026, on the reasoning that there was
 * nothing to switch to) quietly removed club creation from the console for
 * the most common kind of admin: reported by the owner on 25 Sep 2026.
 */
export function canSwitchClub(user) {
  if (!user) return false;
  if (user.superAdmin) return true;
  return (user.adminFor?.length ?? 0) > 0;
}

/**
 * Where `middleware/auth.js` sends a route, or null to let it render.
 *
 * Signed out: sign in, then back to the FULL url (query included) — email deep
 * links into the admin console used to be swapped for /admin. A club admin page
 * for a club this session does not run: /admin.
 *
 * Neither while the API could not be asked who the token is (`unreachable`:
 * /auth/me failed without a 401, so the token still stands). The `user` cookie
 * only says who is signed in, not which clubs they run (utils/sessionUser.js),
 * so judging the session on it during an API restart bounced admins off their
 * own pages, and a token whose cookie was lost read as signed out (X211, the
 * api-restart-logout rule). The page renders; the API still refuses whatever
 * the session may not see, and the browser asks /auth/me again as it starts.
 */
export function authRouteDestination(to, {isAuthenticated, user, hasToken, unreachable}) {
  const held = unreachable && hasToken;
  if (!isAuthenticated) {
    if (held) return null;
    return '/login?authRestricted=true&redirectUrl=' + encodeURIComponent(to.fullPath);
  }
  if (to.meta && to.meta.layout === 'admin' && to.params?.clubId && !canOpenClubAdmin(user, to.params.clubId)) {
    if (held) return null;
    return '/admin';
  }
  return null;
}
